import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  HardDrive,
  Plug,
  ScrollText,
  Database,
  Activity,
  LogOut,
  Shield,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Toaster } from '@/components/ui/sonner'

interface NavSection {
  label: string
  items: Array<{ name: string; href: string; icon: React.ComponentType<{ className?: string }> }>
}

const navigation: NavSection[] = [
  {
    label: 'OVERVIEW',
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    ],
  },
  {
    label: 'ACCOUNTS',
    items: [
      { name: 'Organizations', href: '/orgs', icon: Building2 },
      { name: 'Users', href: '/users', icon: Users },
    ],
  },
  {
    label: 'BILLING',
    items: [
      { name: 'Subscriptions', href: '/billing', icon: CreditCard },
      { name: 'Storage', href: '/storage', icon: HardDrive },
    ],
  },
  {
    label: 'SYSTEM',
    items: [
      { name: 'Connections', href: '/connections', icon: Plug },
      { name: 'Audit', href: '/audit', icon: ScrollText },
      { name: 'Database', href: '/database', icon: Database },
      { name: 'Health', href: '/system', icon: Activity },
    ],
  },
]

function getBreadcrumb(pathname: string): string | null {
  for (const section of navigation) {
    for (const item of section.items) {
      if (item.href === pathname || (item.href !== '/' && pathname.startsWith(item.href))) {
        return item.name
      }
    }
  }
  return null
}

interface AdminLayoutProps {
  children: React.ReactNode
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const breadcrumb = getBreadcrumb(location.pathname)

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Fixed ADMIN banner across the very top */}
      <div
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2 px-4"
        style={{
          height: '28px',
          background: 'hsl(var(--destructive))',
          color: 'hsl(var(--destructive-foreground))',
        }}
      >
        <Shield className="h-3 w-3" />
        <span className="mono-accent font-semibold tracking-widest text-xs uppercase">
          Admin — Platform Control Panel
        </span>
      </div>

      {/* Main layout below the banner */}
      <div className="flex flex-1 pt-7 overflow-hidden w-full">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 flex flex-col border-r bg-card/80 overflow-hidden">
          {/* Brand */}
          <div className="px-5 pt-5 pb-4 shrink-0">
            <NavLink to="/" className="flex items-center gap-2.5 group">
              <div className="w-9 h-9 rounded-lg bg-destructive/10 border border-destructive/20 flex items-center justify-center shrink-0">
                <Shield className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h1 className="brand-wordmark text-sm leading-tight">DiffDrift</h1>
                <p className="kicker mt-0.5 text-destructive/70">Admin</p>
              </div>
            </NavLink>
          </div>

          <div className="mx-3 h-px bg-border/60 shrink-0" />

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
            {navigation.map((section) => (
              <div key={section.label}>
                <p className="mono-accent text-foreground/60 mb-1.5 px-2 tracking-widest font-medium">
                  {section.label}
                </p>
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const isActive =
                      location.pathname === item.href ||
                      (item.href !== '/' && location.pathname.startsWith(item.href))
                    return (
                      <NavLink
                        key={item.href}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-2.5 px-2.5 py-[7px] rounded-md text-sm transition-all duration-150 group relative',
                          isActive
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-foreground/85 hover:bg-accent/60 hover:text-foreground',
                        )}
                      >
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-primary" />
                        )}
                        <item.icon
                          className={cn(
                            'h-4 w-4 shrink-0 transition-colors',
                            isActive
                              ? 'text-primary'
                              : 'text-foreground/70 group-hover:text-foreground',
                          )}
                        />
                        <span className="flex-1">{item.name}</span>
                        {isActive && (
                          <ChevronRight className="h-3 w-3 text-primary/50" />
                        )}
                      </NavLink>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="mx-3 h-px bg-border/60 shrink-0" />

          {/* Footer with user + logout */}
          <div className="p-3 shrink-0">
            <div className="rounded-lg bg-accent/30 p-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{user?.name}</p>
                  <p className="mono-accent text-foreground/60 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={logout}
                  className="p-1.5 rounded-md text-muted-foreground/50 hover:bg-accent hover:text-foreground transition-colors shrink-0"
                  title="Sign out"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Top bar */}
          <header className="h-12 border-b flex items-center gap-3 px-5 shrink-0 bg-card/50">
            {breadcrumb ? (
              <nav className="flex items-center gap-1 text-muted-foreground">
                <span className="text-xs hover:text-foreground transition-colors">
                  Dashboard
                </span>
                {breadcrumb !== 'Dashboard' && (
                  <>
                    <span className="mx-0.5 text-border">/</span>
                    <span className="text-xs font-medium text-foreground">{breadcrumb}</span>
                  </>
                )}
              </nav>
            ) : (
              <span className="text-xs font-medium text-foreground">Dashboard</span>
            )}

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              <span className="mono-accent text-muted-foreground hidden sm:block">
                {user?.email}
              </span>
              <button
                onClick={logout}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              >
                <LogOut className="h-3 w-3" />
                Logout
              </button>
            </div>
          </header>

          {/* Page content */}
          <div
            key={location.pathname}
            className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-6 lg:p-8 animate-page-in"
          >
            {children}
          </div>
        </main>
      </div>
      <Toaster position="bottom-right" />
    </div>
  )
}
